import React, {
  useEffect,
  useState,
} from 'react';
import {
  ArrowRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/outline';
import AnimatedNumber from '../../components/AnimatedNumber';
import Button, {ButtonSizes, ButtonTypes} from '../../components/ButtonNew';

import DocumentHeader from '../../components/DocumentHeader';
import {useAnalytics} from '../../hooks';
import {getBrandName} from '../../components/Brand';

// TikTok-style video slides with realistic creator names
const creatorSlides = [
  {
    id: 'maya',
    creator: '@mayatech.tips',
    platform: 'TikTok',
    videoSrc: 'https://static.tryexponent.com/videos/example_creator_video_2.mp4',
    accentClass: 'from-rose-500/40',
    views: '248K',
    likes: '4.2K',
    comments: '156',
  },
  {
    id: 'jordan',
    creator: '@jordandev',
    platform: 'TikTok',
    videoSrc: 'https://static.tryexponent.com/videos/example_creator_video_1.mp4',
    accentClass: 'from-purple-500/40',
    views: '392K',
    likes: '1.1K',
    comments: '23',
  },
];

// Cycling overlay notifications — pop in/out while videos play to suggest a
// constantly-earning affiliate.
const referralNotifications = [
  {
    initials: 'AS',
    initialsBg: 'from-indigo-500 to-purple-600',
    headline: 'New commission',
    body: 'User upgraded to annual',
    amount: '+$29',
  },
  {
    initials: 'SC',
    initialsBg: 'from-rose-500 to-orange-500',
    headline: 'New commission',
    body: 'User renewed',
    amount: '+$29',
  },
  {
    initials: 'MR',
    initialsBg: 'from-emerald-500 to-cyan-500',
    headline: 'New commission',
    body: 'User bought coaching',
    amount: '+$100',
  },
];

const signUpForm = 'https://airtable.com/shrNjcuPydXbwpb1B';

const steps = [
  {
    name: 'Apply in 2 minutes',
    description: 'Fill out our quick form and hear back within 3 business days.',
  },
  {
    name: 'Get your link + brand kit',
    description: 'Get a custom tracking link and brand assets you can use.',
  },
  {
    name: 'Earn 20% commission',
    description: 'Get commission every month, including subscription renewals.',
  },
];

// Fill with real creator quotes to light up the testimonials section.
// Shape: {quote: string, name: string, outlet: string}
// Section renders null when this array is empty.
const testimonials = [];

const faqs = [
  {
    question: `What does ${getBrandName()} offer?`,
    answer: `${getBrandName()} is an online platform that offers the most up-to-date courses for people in tech careers such as product, engineering, data, and more. We offer expert-written guides, AI practice tools, and expert 1-on-1 interview prep coaching.`,
  },
  {
    question: `Who is the ${getBrandName()} affiliate program for?`,
    answer: 'We partner with all kinds of businesses, creators, bloggers, career coaches, podcasters, and more. Basically anyone with an audience that cares about tech careers, interview prep, or professional growth. Not sure if you qualify? Just reach out and ask at affiliates@aced.io, we love hearing from folks doing interesting things.',
  },
  {
    question: 'How much can I earn?',
    answer: 'You earn 20% commission on all purchases your referrals make. This includes 20% of membership subscriptions (recurring every month they stay subscribed) and 20% of services like coaching sessions. There is no cap on total earnings, and successful affiliates can earn thousands per month.',
  },
  {
    question: 'When do payouts happen?',
    answer: 'Payouts are processed monthly once your balance clears the minimum threshold. We support multiple payment methods globally, and you\'ll get the details at approval.',
  },
  {
    question: 'Can you provide marketing assets?',
    answer: 'Yes, every affiliate will receive a brand kit, product screenshots, and helpful templates. We\'re happy to make custom assets for top partners too.',
  },
  {
    question: 'How does attribution work?',
    answer: 'We use a 30-day cookie attribution window. This means if someone clicks your affiliate link, you\'ll get credit for any purchase they make within 30 days, even if they don\'t buy immediately.',
  },
  {
    question: 'What tracking and analytics are available?',
    answer: 'You\'ll have access to a comprehensive dashboard with real-time data including clicks, conversions, earnings, and detailed analytics about your referral performance. Track which content and channels drive the most conversions to optimize your strategy.',
  },
  {
    question: 'Can you offer a discount to my audience?',
    answer: 'We occasionally offer promotions for new partners and special events. Feel free to reach out at affiliates@aced.io to discuss more.',
  },
];

// Drives a counter from `start` → `target` over `durationMs`, plus an eased
// progress value (0→1) for syncing other animations (chart fill, dot trail).
function useCountUp(start, target, durationMs = 1800, key = 0) {
  const [value, setValue] = useState(start);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let raf;
    let begin = null;
    const step = (ts) => {
      if (begin === null) begin = ts;
      const p = Math.min((ts - begin) / durationMs, 1);
      // Smooth ease-out for a satisfying ticker feel.
      const eased = 1 - (1 - p) ** 3;
      setProgress(eased);
      setValue(Math.round(start + (target - start) * eased));
      if (p < 1) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [start, target, durationMs, key]);
  return {value, progress};
}

function MiniRevenueChart({progress = 1}) {
  // Sparkline-style chart of monthly revenue trending up. The line + fill are
  // revealed left-to-right via a clipPath driven by `progress`.
  const points = [8, 14, 12, 22, 28, 26, 36, 44, 50, 58, 70, 84];
  const width = 160;
  const height = 32;
  const padY = 4;
  const max = Math.max(...points);
  const stepX = width / (points.length - 1);
  const path = points
    .map((p, i) => {
      const x = i * stepX;
      const y = height - (p / max) * (height - padY * 2) - padY;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const areaPath = `${path} L ${width} ${height} L 0 ${height} Z`;

  // Position a "current value" dot at the leading edge of the reveal.
  const xRevealed = Math.max(Math.min(width * progress, width), 0);
  const fIdx = Math.min(xRevealed / stepX, points.length - 1);
  const lo = Math.floor(fIdx);
  const hi = Math.min(lo + 1, points.length - 1);
  const t = fIdx - lo;
  const valAt = points[lo] + (points[hi] - points[lo]) * t;
  const yRevealed = height - (valAt / max) * (height - padY * 2) - padY;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className="w-full h-7"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="aff-spark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366F1" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
        </linearGradient>
        <clipPath id="aff-spark-clip">
          <rect x="0" y="0" width={xRevealed} height={height} />
        </clipPath>
      </defs>
      <g clipPath="url(#aff-spark-clip)">
        <path d={areaPath} fill="url(#aff-spark)" />
        <path
          d={path}
          fill="none"
          stroke="#4F46E5"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      {progress > 0 && progress < 1 && (
        <circle cx={xRevealed} cy={yRevealed} r="3.5" fill="#4F46E5" stroke="white" strokeWidth="1.5" />
      )}
    </svg>
  );
}

function CyclingNotification() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const cycle = setInterval(() => {
      setVisible(false);
      const swap = setTimeout(() => {
        setIdx((i) => (i + 1) % referralNotifications.length);
        setVisible(true);
      }, 220);
      return () => clearTimeout(swap);
    }, 2800);
    return () => clearInterval(cycle);
  }, []);

  const n = referralNotifications[idx];
  return (
    <div
      className={`rounded-xl bg-white shadow-xl ring-1 ring-gray-200 p-3 transition-all duration-200 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`h-8 w-8 rounded-full bg-gradient-to-br ${n.initialsBg} flex items-center justify-center text-white text-[11px] font-bold shrink-0`}
        >
          {n.initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold text-gray-900 truncate">{n.headline}</div>
          <div className="text-[11px] text-gray-500 truncate">{n.body}</div>
        </div>
        {n.amount && (
          <div className="text-xs font-bold text-green-600 shrink-0">{n.amount}</div>
        )}
      </div>
    </div>
  );
}

function CarouselSlide({slide, isTransitioning, isActive}) {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    if (isActive && videoRef.current) {
      videoRef.current.play();
    } else if (!isActive && !isTransitioning) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isActive, isTransitioning]);

  return (
    <>
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover bg-black"
        src={slide.videoSrc}
        muted
        playsInline
        preload="metadata"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none"
        aria-hidden="true"
      />

      {/* TikTok UI Elements - fade during transition */}
      <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {/* Right side actions - simplified without profile and share */}
        <div className="absolute right-2 bottom-20 flex flex-col items-center">
          {/* Like */}
          <div className="flex flex-col items-center">
            <div className="w-10 h-8 flex items-center justify-center">
              <svg className="w-5 h-5 text-white opacity-70" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
              </svg>
            </div>
            <span className="text-white text-[10px] font-semibold opacity-90">{slide.likes || '4.2K'}</span>
          </div>

          {/* Comment */}
          <div className="flex flex-col items-center">
            <div className="w-10 h-8 flex items-center justify-center">
              <svg className="w-5 h-5 text-white opacity-70" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-white text-[10px] font-semibold opacity-90">{slide.comments || '156'}</span>
          </div>
        </div>

        {/* Bottom caption - moved up to avoid clipping */}
        <div className="absolute inset-x-0 bottom-15 p-4 pr-16 text-white">
          <div className="text-xs font-bold mb-1">{slide.creator}</div>
          <div className="w-2/3 h-2 bg-white/20 rounded-full mt-1" />
          <div className="w-1/2 h-2 bg-white/20 rounded-full mt-1.5" />
        </div>
      </div>
    </>
  );
}

// TikTok-style vertical carousel.
//
// Strategy: every slide is rendered exactly once with a stable
// `key={slide.id}`, so each <video> element persists across cycles — no
// remount, no reload, no double-play. All slides sit inside a single wrapper
// that translates upward by 100% during each transition. When the
// transition finishes, we _atomically_ (in a single render) snap the wrapper
// back to translateY(0%) with transitions disabled and advance `tick`,
// which rotates every slide's `top` slot one step earlier in the cycle.
//
// The slide that just exited the top (visual = -100%) lands at
// top = (len - 1) * 100% — the bottom of the queue — but because the snap
// happens with `transition: none` and the only currently visible slide
// hasn't moved, the swap is invisible. The whole stack scrolls upward
// forever, even when len === 2, where pure modular wrap-around math would
// otherwise collapse and animate the slide back downward.
function CreatorVideoCarousel({slides, intervalMs = 6000, transitionMs = 600}) {
  const [{tick, animating, transitionEnabled}, setCarouselState] = useState({
    tick: 0,
    animating: false,
    transitionEnabled: true,
  });
  const len = slides.length;

  useEffect(() => {
    if (len <= 1) return undefined;
    const id = setInterval(() => {
      // Phase 1 — start the upward slide animation.
      setCarouselState((s) => ({...s, animating: true, transitionEnabled: true}));
      // Phase 2 — when the animation ends, atomically: snap the wrapper
      // back to 0 (transitions off), advance the tick, and rotate the slot
      // positions. All three changes land in one render, so the browser
      // composites the new state in a single paint with no flicker.
      setTimeout(() => {
        setCarouselState((s) => ({
          tick: s.tick + 1,
          animating: false,
          transitionEnabled: false,
        }));
      }, transitionMs + 30);
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, transitionMs, len]);

  // After the snap has painted, re-enable transitions on the next frame so
  // the following tick animates normally.
  useEffect(() => {
    if (transitionEnabled) return undefined;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setCarouselState((s) => ({...s, transitionEnabled: true}));
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [transitionEnabled]);

  const activeIdx = ((tick % len) + len) % len;

  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <div
        className="absolute inset-0"
        style={{
          transform: `translate3d(0, ${animating ? -100 : 0}%, 0)`,
          transition: transitionEnabled
            ? `transform ${transitionMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
            : 'none',
          willChange: 'transform',
        }}
      >
        {slides.map((slide, i) => {
          const slotPos = (((i - tick) % len) + len) % len;
          return (
            <div
              key={slide.id}
              className="absolute left-0 right-0 h-full"
              style={{top: `${slotPos * 100}%`}}
            >
              <CarouselSlide
                slide={slide}
                isTransitioning={animating}
                isActive={i === activeIdx}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AffiliateMetricsCard() {
  const [earnings, setEarnings] = useState(0);
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    // Brief delay before starting animation
    const timer = setTimeout(() => {
      setEarnings(1490);
      setChartKey(Date.now());
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Use the custom hook for chart progress only
  const chartProgress = useCountUp(0, 1, 1800, chartKey);
  const referrals = earnings > 0 ? 37 : 0;

  return (
    <div className="rounded-lg bg-white shadow-2xl ring-1 ring-gray-200 overflow-y-hidden">
      <div className="p-3 bg-gradient-to-br from-white to-indigo-50/40">
        <div className="flex items-center justify-between mb-0.5">
          <div className="text-[9px] uppercase tracking-wide text-gray-500 font-semibold">
            Monthly earnings
          </div>
        </div>
        <div className="text-xl font-extrabold text-gray-900 leading-tight tabular-nums h-6 flex items-center">
          <span className="mr-0.5">$</span>
          <AnimatedNumber value={earnings} />
        </div>
        <div className="mt-1">
          <MiniRevenueChart progress={chartProgress.progress} />
        </div>
        <div className="flex items-center justify-between text-[8px] text-gray-500 -mt-1 mb-1">
          {/* <span>Jan</span>
          <span>Today</span> */}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1.5 border-t border-gray-100">
          <div>
            <div className="text-[8px] uppercase tracking-wide text-gray-500 font-semibold">
              Referrals
            </div>
            <div className="text-xs font-extrabold text-gray-900 tabular-nums">
              {referrals}
            </div>
          </div>
          <div>
            <div className="text-[8px] uppercase tracking-wide text-gray-500 font-semibold">
              Conversion
            </div>
            <div className="text-xs font-extrabold text-gray-900">8.3%</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreatorVideoHero() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Parallax offsets - different depths
  const phoneOffset = scrollY * 0.03; // Subtle parallax for phone
  const notificationOffset = scrollY * 0.05; // Original parallax for notification
  const metricsOffset = scrollY * 0.1; // Original parallax for metrics

  return (
    <div className="relative w-full h-[560px] overflow-visible pt-4">
      {/* iPhone container with vertical clipping only */}
      <div
        className="absolute left-[20%] top-8 z-10 h-full"
      >
        {/* iPhone frame */}
        <div className="relative w-[320px] h-[600px] origin-top-right shadow-2xl" style={{transform: `translateY(${-phoneOffset}px)`}}>
          {/* Phone bezel */}
          <div className="absolute inset-0 rounded-[3.2rem] bg-gray-900 p-[4px] ">
            {/* Screen area */}
            <div className="relative h-full w-full rounded-[3rem] overflow-hidden bg-black">
              {/* Status bar */}
              <div className="absolute top-0 left-0 right-0 h-14 bg-gradient-to-b from-black/50 to-transparent z-20 flex items-center justify-between px-7 pt-3">
                <span className="text-white/70 mt-1 text-xs font-semibold">10:10</span>
              </div>

              {/* TikTok carousel */}
              <CreatorVideoCarousel slides={creatorSlides} intervalMs={6000} transitionMs={600} />
            </div>
          </div>

          {/* Dynamic Island / Notch */}
          <div className="absolute top-7 left-1/2 -translate-x-1/2 w-30 h-6 bg-gray-900 rounded-3xl z-30" />
        </div>
      </div>

      {/* Floating notification with parallax - overlapping halfway with video on right */}
      <div
        className="absolute right-10 top-24 w-[250px] z-20"
        style={{transform: `translateY(${-notificationOffset}px)`}}
      >
        <CyclingNotification />
      </div>

      {/* Metrics card with parallax - positioned near bottom of hero */}
      <div
        className="absolute left-[40%] bottom-16 w-[220px] z-20 -translate-x-1/2"
        style={{transform: `translate(-50%, ${-metricsOffset}px)`}}
      >
        <AffiliateMetricsCard />
      </div>
    </div>
  );
}

function FaqRow({question, answer}) {
  const [open, setOpen] = useState(false);
  const contentId = `faq-answer-${question.toString().replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="border-b border-gray-200">
      <button
        type="button"
        className="w-full flex justify-between items-center py-5 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={contentId}
      >
        <span className="text-base md:text-lg font-medium text-gray-900">{question}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-gray-500 transform transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div id={contentId} className="pb-5 pr-8 text-base text-gray-500">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function AffiliateLandingPage() {
  const {tracker} = useAnalytics();

  useEffect(() => { tracker.affiliateLandingView(); }, []);

  return (
    <div className="relative bg-white overflow-hidden">
      <DocumentHeader
        title={`Become an ${getBrandName()} Affiliate`}
        socialDescription="Help people land their dream tech job and and earn a great commission doing it!"
      />
      <div className="relative pt-6 pb-16 sm:pb-24">
        <main className="w-full px-2 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 overflow-hidden">
            <div className="mt-8 sm:mt-20 text-center sm:text-left">
              <p className="text-sm font-semibold tracking-widest text-indigo uppercase">
                Affiliate Partners
              </p>
              <h1 className="mt-4 text-3xl sm:text-4xl tracking-tight font-extrabold text-gray-900 text-pretty sm:text-5xl">
                The affiliate program for top tech creators.
              </h1>
              <p className="mt-5 text-base text-gray-500 sm:text-lg md:text-xl max-w-2xl">
                {`Share ${getBrandName()} with your audience and earn 20%
                recurring commission on sales every month.`}
              </p>
              <div className="mt-8">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    size={ButtonSizes.LARGE}
                    href={signUpForm}
                    target="_blank"
                    className="px-8 py-4 text-lg font-semibold inline-flex items-center justify-center gap-2"
                  >
                    Apply now
                    <ArrowRightIcon className="h-5 w-5" />
                  </Button>
                  <Button
                    size={ButtonSizes.LARGE}
                    buttonType={ButtonTypes.SECONDARY}
                    href="#how-it-works"
                    className="px-8 py-4 text-lg font-semibold"
                  >
                    How it works
                  </Button>
                </div>
                {/* Social proof */}
                <div className="flex flex-col justify-center items-center sm:items-start mt-8 mb-8">
                  <p className="text-sm text-gray-500 font-medium mb-3">Trusted by 100+ partners on top platforms</p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <img src="/images/creator-platforms/youtube.svg" alt="YouTube" className="h-6 w-6 grayscale opacity-50" />
                      <span className="text-md font-semibold text-gray-700">YouTube</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <img src="/images/creator-platforms/tiktok.svg" alt="TikTok" className="h-6 w-6 grayscale opacity-50" />
                      <span className="text-sm font-semibold text-gray-700">TikTok</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <img src="/images/creator-platforms/linkedin.svg" alt="LinkedIn" className="h-6 w-6 grayscale opacity-50" />
                      <span className="text-sm font-semibold text-gray-700">LinkedIn</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <img src="/images/creator-platforms/medium.svg" alt="Medium" className="h-6 w-6 grayscale opacity-50" />
                      <span className="text-sm font-semibold text-gray-700">Medium</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Hide the entire video/dashboard column on mobile — keeps the page
                light and avoids loading video elements on small screens. */}
            <div className="hidden lg:block relative">
              <CreatorVideoHero />
            </div>
          </div>
        </main>

        <section id="how-it-works" className="bg-gray-50 w-full">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl tracking-tight font-extrabold text-gray-900">
                Start earning in three easy steps
              </h2>
            </div>
            <div className="relative">
              <div
                aria-hidden="true"
                className="hidden md:block absolute top-5 left-[16.67%] right-[16.67%] h-px bg-indigo-200"
              />
              <ol className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
                {steps.map((step, num) => (
                  <li key={step.name} className="relative bg-white rounded-xl p-6 pt-12 text-center border border-gray-200 shadow-sm">
                    <span className="absolute left-1/2 -translate-x-1/2 -top-5 flex items-center justify-center rounded-full bg-indigo text-white w-10 h-10 text-base font-bold ring-8 ring-white">
                      {num + 1}
                    </span>
                    <p className="text-lg font-bold text-gray-900">{step.name}</p>
                    <p className="mt-2 text-base text-gray-500">{step.description}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {testimonials.length > 0 && (
          <section className="bg-gray-50">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl tracking-tight font-extrabold text-gray-900">
                  Creators who partner with us
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((t) => (
                  <figure key={t.name} className="bg-white rounded-xl p-6 border border-gray-200">
                    <blockquote className="text-base md:text-lg text-gray-700">
                      &ldquo;
                      {t.quote}
                      &rdquo;
                    </blockquote>
                    <figcaption className="mt-4 flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-200" aria-hidden="true" />
                      <div>
                        <div className="text-sm font-bold text-gray-900">{t.name}</div>
                        <div className="text-sm text-gray-500">{t.outlet}</div>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl tracking-tight font-extrabold text-gray-900">
              Frequently asked
            </h2>
          </div>
          <div className="border-t border-gray-200">
            {faqs.map((f) => (
              <FaqRow key={f.question} question={f.question} answer={f.answer} />
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 my-16">
          <div className="mx-auto max-w-7xl relative overflow-hidden rounded-3xl px-6 py-14 md:py-16 text-center text-white bg-[linear-gradient(135deg,var(--color-indigo-900),var(--color-indigo))]">
            <div
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.1),transparent_50%)]"
              aria-hidden="true"
            />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                Ready to start earning?
              </h2>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                {/* FIXME: Button inversion override */}
                <Button
                  href={signUpForm}
                  target="_blank"
                  className="!bg-white !text-indigo px-7 py-3.5 text-base md:text-lg md:px-8 md:py-4 rounded-lg inline-flex items-center gap-2"
                >
                  Apply now →
                </Button>
              </div>
              <p className="mt-8 text-sm opacity-80">
                {'Questions? Email '}
                <a className="underline" href="mailto:affiliates@aced.io">affiliates@aced.io</a>
              </p>
              <p className="mt-1 text-sm opacity-80">
                {'Interested in other types of partnerships? Email '}
                <a className="underline" href="mailto:partnerships@aced.io">partnerships@aced.io</a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
